import AppPage from "layouts/AppPage";
import { useCallback, useEffect, useMemo, useState } from "react";
import { HStack, VStack, Badge, Image } from "@chakra-ui/react";
import CDS from "design-system";
import { useNavbar } from "hooks/useNavbar";
import { useLocation, useNavigate } from "react-router";
import NewHireJourneysList from "features/NewHireJourney/Components/NewHireJourneysList";
import PendingNewHireJourneysTable from "features/NewHireJourney/Components/PendingNewHireJourneysTable";
import JourneyActionItemsDrawer from "features/NewHireJourney/Components/JourneyActionItemsDrawer";
import { Heading18 } from "components/Heading";
import NewHireJourneysFilters, {
  NewHireJourneysFilters as FiltersType,
} from "features/NewHireJourney/Components/NewHireJourneysFilters";
import FilterSortSearchBar from "components/ControlBar/FilterSortSearchBar";
import { TabOption, TabSelector } from "components/TabSelector";
import { usePendingNewHireJourneysCountQuery } from "features/PendingNewHire/hooks";
import { INTERNAL_TEAM_ROUTES } from "definitions/constants/routeConstants";
import useSideDrawer from "hooks/useSideDrawer";
import { NewHireJourney } from "models/v2/newHireJourney";
import EmptyState from "components/EmptyComponents/EmptyState";
import CoworkersSVG from "assets/undraw_co_workers.svg";
import CalendarSVG from "assets/undraw_calendar.svg";
import CelebrationSVG from "assets/undraw_celebration.svg";

type ViewMode = "active" | "pending" | "completed";

const defaultJourneyFilters: FiltersType = {};

const sortOptions = [
  { label: "Start Date: Newest First", value: "start_date_desc" },
  { label: "Start Date: Oldest First", value: "start_date_asc" },
  { label: "Name: A-Z", value: "name_asc" },
  { label: "Name: Z-A", value: "name_desc" },
];

const defaultSortString = "start_date_desc";

function viewModeFromPath(pathname: string): ViewMode {
  if (pathname.includes("/pending")) return "pending";
  if (pathname.includes("/completed")) return "completed";
  return "active";
}

export default function NewHireJourneysPage() {
  const { setPageTitle } = useNavbar();
  const location = useLocation();
  const navigate = useNavigate();

  const viewMode: ViewMode = viewModeFromPath(location.pathname);

  const [filters, setFilters] = useState<FiltersType>(defaultJourneyFilters);
  const [sort, setSort] = useState<string>(defaultSortString);

  const { data: pendingCountData } = usePendingNewHireJourneysCountQuery();
  const pendingCount = pendingCountData?.count || 0;

  const drawer = useSideDrawer<NewHireJourney, string>({
    getItemId: (journey) => (journey ? String(journey.id) : undefined),
  });

  useEffect(() => {
    setPageTitle("Journeys");
    return () => {
      setPageTitle(null);
    };
  }, [setPageTitle]);

  const handleFiltersChange = useCallback((newFilters: FiltersType) => {
    setFilters(newFilters);
  }, []);

  const handleReset = useCallback(() => {
    setFilters(defaultJourneyFilters);
  }, []);

  const handleSortChange = useCallback((value: string) => {
    setSort(value || defaultSortString);
  }, []);

  const handleViewModeChange = useCallback(
    (value: ViewMode) => {
      if (value === "completed") {
        setFilters(defaultJourneyFilters);
      }
      if (value === "pending") navigate(INTERNAL_TEAM_ROUTES.newHireJourneys.pending.index, { replace: true });
      else if (value === "completed") navigate(INTERNAL_TEAM_ROUTES.newHireJourneys.completed.index, { replace: true });
      else navigate(INTERNAL_TEAM_ROUTES.newHireJourneys.index, { replace: true });
    },
    [navigate],
  );

  const activeEmptyState = useMemo(() => (
    <EmptyState message="No active journeys">
      <Image maxWidth="240px" maxHeight="240px" src={CoworkersSVG} />
    </EmptyState>
  ), []);

  const pendingEmptyState = useMemo(() => (
    <EmptyState message={"All caught up!\nNo journeys waiting to be kicked off."}>
      <Image maxWidth="240px" maxHeight="240px" src={CalendarSVG} />
    </EmptyState>
  ), []);

  const completedEmptyState = useMemo(() => (
    <EmptyState message="No completed journeys yet">
      <Image maxWidth="240px" maxHeight="240px" src={CelebrationSVG} />
    </EmptyState>
  ), []);

  const additionalQueries = useMemo(() => {
    const queries: Record<string, string | string[]> = {};

    if (filters.q && filters.q.length > 2) {
      queries.q = filters.q;
    }

    if (filters.with_active_issues === "true") {
      queries.with_active_issues = "true";
    }

    if (filters.with_open_tasks === "true") {
      queries.with_open_tasks = "true";
    }

    if (filters.with_overdue_tasks === "true") {
      queries.with_overdue_tasks = "true";
    }

    if (filters.stage) {
      queries.stage = filters.stage;
    }

    if (sort) {
      const sep = sort.lastIndexOf("_");
      queries["sort[0][sort_by]"] = sort.slice(0, sep);
      queries["sort[0][direction]"] = sort.slice(sep + 1);
    }

    if (viewMode === "active") {
      queries.statuses = "upcoming,active";
    } else if (viewMode === "completed") {
      queries.statuses = "archived";
    }

    return queries;
  }, [filters, sort, viewMode]);

  return (
    <AppPage>
      <VStack spacing={6} align="stretch">
        <VStack mt="4" spacing={3} align="flex-start">
          <Heading18>Journeys</Heading18>
          <TabSelector value={viewMode} onChange={(value) => handleViewModeChange(value)}>
            <TabOption value="active">Active</TabOption>
            <TabOption value="pending">
              <HStack spacing={2}>
                <CDS.Text12 as="span" fontWeight="semibold">
                  Pending
                </CDS.Text12>
                {pendingCount > 0 && (
                  <Badge bg="cdsOrange.300" borderRadius="full">
                    {pendingCount}
                  </Badge>
                )}
              </HStack>
            </TabOption>
            <TabOption value="completed">Completed</TabOption>
          </TabSelector>
        </VStack>

        {(viewMode === "active" || viewMode === "completed") && (
          <>
            {viewMode === "active" && (
              <NewHireJourneysFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onReset={handleReset}
                currentSort={sort}
                defaultSort={defaultSortString}
                onSortChange={handleSortChange}
                sortOptions={sortOptions}
              />
            )}
            {viewMode === "completed" && (
              <FilterSortSearchBar
                searchPlaceholder="Search new hires..."
                searchValue={filters.q}
                onSearchChange={(value) => setFilters({ ...filters, q: value === "" ? undefined : value })}
                currentSort={sort}
                defaultSort={defaultSortString}
                onSortChange={handleSortChange}
                sortOptions={sortOptions}
                activeFilters={[]}
                onReset={handleReset}
                hasActiveFilters={false}
              />
            )}
            <NewHireJourneysList
              additionalQueries={additionalQueries}
              onSelectJourney={drawer.expandClickHandlerByItem}
              emptyStateComponent={viewMode === "active" ? activeEmptyState : completedEmptyState}
            />
          </>
        )}

        {viewMode === "pending" && (
          <PendingNewHireJourneysTable emptyStateComponent={pendingEmptyState} />
        )}
      </VStack>
      <JourneyActionItemsDrawer
        journey={drawer.selectedItem}
        isOpen={drawer.isOpen}
        onClose={drawer.onClose}
      />
    </AppPage>
  );
}
